import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { MapPin, Loader2, Play } from "lucide-react";

import { fmtDate, fmtDateShort, normDomain } from "../../lib/format";
import GbpCard from "./local/GbpCard";
import LocalPackTrendChart from "./local/LocalPackTrendChart";
import LocalCompetitorsCard from "./local/LocalCompetitorsCard";
import LocalPackHeatmap from "./local/LocalPackHeatmap";

/**
 * Local SEO tab — orchestrates GBP / competitor / heatmap / trend fetches for a local
 * client and lays out the `local/*` subcomponents. Rendering split into focused pieces
 * (GbpCard / TrendChart / Heatmap / Competitors) so this file stays scannable.
 */
export default function LocalSEOTab({ client, reloadClient }) {
  const { isAdmin } = useAuth();
  const [gbp, setGbp] = useState(null);
  const [gbpLoading, setGbpLoading] = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [rankSnaps, setRankSnaps] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [running, setRunning] = useState(false);
  const [addingDomain, setAddingDomain] = useState(null);

  const isLocal = client?.campaign_type === "local";

  // Normalised set of competitor domains already saved on the client.
  const trackedSet = useMemo(() => {
    const out = new Set();
    for (const d of (client?.competitors || [])) out.add(normDomain(d));
    return out;
  }, [client?.competitors]);

  const fetchAll = async () => {
    const [g, c, s, h] = await Promise.all([
      api.get(`/clients/${client.id}/gbp`),
      api.get(`/clients/${client.id}/local-competitors`),
      api.get(`/clients/${client.id}/rank-snapshots?limit=24`),
      api.get(`/clients/${client.id}/local-finder/heatmap?limit=12`),
    ]);
    setGbp(g.data);
    setCompetitors(c.data.competitors || []);
    setSnapshotAt(c.data.snapshot_at || g.data.snapshot_at);
    setRankSnaps(s.data.snapshots || []);
    setHeatmap(h.data);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isLocal) {
        if (alive) setGbpLoading(false);
        return;
      }
      if (alive) setGbpLoading(true);
      try {
        await fetchAll();
      } catch {
        // silent — sub-cards render empty states themselves
      } finally {
        if (alive) setGbpLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id, isLocal]);

  const runFinder = async () => {
    setRunning(true);
    const t = toast.loading("Running Local Finder for all keywords… (may take 30–60s)");
    try {
      await api.post(`/clients/${client.id}/local-finder/run`);
      await fetchAll();
      toast.success("Local Finder refreshed.", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Local Finder failed", { id: t });
    } finally {
      setRunning(false);
    }
  };

  const addCompetitor = async (domain) => {
    const d = normDomain(domain);
    if (!d || trackedSet.has(d)) return;
    setAddingDomain(d);
    try {
      const updated = [...(client.competitors || []), d];
      await api.patch(`/clients/${client.id}`, { competitors: updated });
      toast.success(`Added ${d} as competitor`);
      if (reloadClient) await reloadClient();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add competitor");
    } finally {
      setAddingDomain(null);
    }
  };

  // Map-pack vs Organic trend series (one point per rank snapshot, oldest → newest).
  const trendSeries = useMemo(() => {
    return [...rankSnaps].reverse().map((s) => {
      const results = s.results || [];
      const totalKw = results.length || 1;
      const avg = (arr) => (arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null);
      return {
        at: s.at,
        label: fmtDateShort(s.at),
        avg_organic: avg(results.map((r) => r.position).filter((p) => p != null && p <= 100)),
        avg_local: avg(results.map((r) => r.local_pack_position).filter((p) => p != null)),
        in_pack: results.filter((r) => r.local_pack_position != null).length,
        pack_visible: results.filter((r) => r.local_pack_present).length,
        top3_organic: results.filter((r) => r.position != null && r.position <= 3).length,
        top10_organic: results.filter((r) => r.position != null && r.position <= 10).length,
        total: totalKw,
      };
    });
  }, [rankSnaps]);

  if (!isLocal) {
    return (
      <div className="card-soft p-6 text-center">
        <div className="inline-flex h-10 w-10 rounded-xl bg-[#f7fafc] text-[#a0aec0] items-center justify-center mb-3">
          <MapPin className="h-4 w-4" />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Local SEO</div>
        <h3 className="font-display text-base mt-1 text-[#1a202c]">This client is a national campaign</h3>
        <p className="text-xs text-[#718096] mt-2 max-w-md mx-auto">
          Switch the campaign type to <span className="font-semibold text-[#1a202c]">Local</span> on the client form,
          add a service location (city or postcode), then run a rank check to populate this tab.
        </p>
      </div>
    );
  }

  // Hyper-local vs centroid badge — uses the most recent snapshot in the heatmap window.
  const latestSnap = heatmap?.snapshots?.[heatmap.snapshots.length - 1];
  const isHyperLocal = latestSnap?.search_origin === "coordinate";

  return (
    <div className="space-y-5" data-testid="local-seo-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Local SEO</div>
          <h2 className="font-display text-2xl mt-0.5 text-[#1a202c]">
            Map presence · {client.service_location?.split(",")[0]}
          </h2>
          <p className="text-xs text-[#718096] mt-1 flex items-center gap-2 flex-wrap">
            <span>Auto-runs weekly. Last snapshot: {snapshotAt ? fmtDate(snapshotAt) : "never"}.</span>
            {isHyperLocal && (
              <span
                className="text-[10px] uppercase tracking-widest font-bold text-[#234e52] bg-[#4fd1c5]/15 border border-[#4fd1c5]/40 px-2 py-0.5 rounded-md"
                data-testid="local-finder-mode-hyper"
                title={`Searching from ${latestSnap.location_coordinate}`}
              >
                Hyper-local · {client.search_radius_km || 5} km
              </span>
            )}
            {latestSnap && !isHyperLocal && (
              <span
                className="text-[10px] uppercase tracking-widest font-bold text-[#744210] bg-[#fefcbf]/80 border border-[#f6e05e]/60 px-2 py-0.5 rounded-md"
                data-testid="local-finder-mode-centroid"
                title="Add a GBP CID and click Test to capture coordinates"
              >
                Town centroid
              </span>
            )}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={runFinder}
            disabled={running || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="local-finder-run"
            className="btn-primary inline-flex items-center gap-2"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {running ? "Running…" : "Refresh Local Finder"}
          </button>
        )}
      </div>

      <GbpCard client={client} gbp={gbp} loading={gbpLoading} />

      <LocalPackTrendChart series={trendSeries} />

      <LocalPackHeatmap data={heatmap} client={client} />

      <LocalCompetitorsCard
        competitors={competitors}
        trackedSet={trackedSet}
        addingDomain={addingDomain}
        onAddCompetitor={addCompetitor}
      />
    </div>
  );
}
